import React from "react";

import classnames from "classnames";
import isIntuit from "Intuit/isIntuit";
import { map } from "lodash";
import PropTypes from "prop-types";
import Select, { components } from "react-select";

import Icon from "Common/Icon";

import makeBem from "Utils/makeBem";

const bem = makeBem("SIFormSelectWithSearch");
type Props = {
  className?: string;
  placeholder?: string;
  // Either { [label]: value } or Array<[value, label]>.  Array form is
  // preferred because it is ordered and allows React.Node as the label.
  options: Record<string, string> | Array<[any, any]>;
  name: string;
  input?: any;
  iconName?: string;
  meta?: {
    touched: boolean;
    error?: any;
    warning?: any;
  };
};

const SIFormSelectWithSearch = ({ className, placeholder, options, name, input, meta, iconName, ...rest }: Props) => {
  const classes = classnames(bem(), className, {
    [bem(null, "intuit")]: isIntuit(),
    [bem(null, "error-intuit")]: isIntuit() && meta && meta.touched && meta.error !== undefined,
  });

  if (!Array.isArray(options)) {
    options = map(options, (value, label) => [value, label]);
  }

  const handleChange = (newValue) => {
    if (!input) {
      input = {};
    }

    // Workaround for single-select mode where manually deleting value results in an empty array, not `null`
    if (Array.isArray(newValue) && !newValue.length) {
      return null;
    }

    // Oddly, the entire { value, label } object is provided as a new value (or array of them)
    // So, we'll manually convert to just values
    if (newValue && !Array.isArray(newValue)) {
      newValue = [newValue];
    }

    const newVal = newValue.map((obj) => obj.value)[0];
    input.onChange(newVal);
  };

  // Custom blur handler for bug workarounds
  const handleBlur = (input) => {
    if (!input) {
      input = {};
    }

    // Workaround for https://github.com/JedWatson/react-select/issues/805
    input.onBlur(input.value);
  };

  const defaultValue = (input, options) => {
    // $FlowFixMe
    const defaultOption = options.find(([option]) => option === input.value);
    return defaultOption
      ? {
          value: defaultOption[0],
          label: defaultOption[1],
        }
      : undefined;
  };

  const ValueContainer = ({ children, ...props }) => (
    <components.ValueContainer {...props}>
      {!!children && (
        <Icon
          icon={iconName}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: isIntuit() ? 6 : 16,
          }}
          mr={2}
          size="lg"
        />
      )}
      {children}
    </components.ValueContainer>
  );

  const styles = {
    valueContainer: (base) => ({ ...base, paddingLeft: 24, marginRight: 20 }),
    placeholder: (base) => ({
      ...base,
      ...{
        ...(iconName && {
          marginLeft: isIntuit() ? 36 : 24,
        }),
      },
    }),
    input: (base) => ({
      ...base,
      ...{
        ...(iconName && {
          marginLeft: isIntuit() ? 36 : 24,
        }),
      },
    }),
  };
  return (
    // $FlowFixMe
    <Select
      {...rest}
      name={name}
      onChange={handleChange}
      onBlur={() => handleBlur(input)}
      defaultValue={defaultValue(input, options)}
      className={classes}
      classNamePrefix={bem()}
      options={options.map(([optionValue, label]) => ({
        value: optionValue,
        label,
      }))}
      {...(iconName && {
        components: {
          ...{
            ValueContainer,
          },
        },
      })}
      styles={styles}
    />
  );
};

SIFormSelectWithSearch.propTypes = {
  placeholder: PropTypes.string,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};
export default SIFormSelectWithSearch;
